import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  map,
  tap,
  switchMap,
  catchError,
  withLatestFrom
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { from, Observable, of, pipe } from 'rxjs';
import { notificationMessages } from '@care-portals/data';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { APIService } from '../../services/api/api-service';
import * as AccountActions from '../account/account.actions';
import * as CheckoutActions from './checkout.actions';
import * as CheckoutSelectors from './checkout.selectors';
import { Router } from '@angular/router';
import { CollectJsService } from '../../services/collectjs-service';
import { OrgStripeService } from '../../services/org-stripe.service';
import { isValidCVV } from '@care-portals/shared';
import { ICustomerUser } from '@appgen-health-crm/api-interfaces';
import * as AuthSelectors from '../../auth/+store/auth.selectors';
import * as AppSelectors from '../app/app.selectors';

@Injectable()
export class CheckoutV2Effects {
  constructor(
    private actions$: Actions,
    private notification: NzNotificationService,
    private apiService: APIService,
    private gtm: Angulartics2GoogleTagManager,
    private store: Store,
    private router: Router,
    private collectJsService: CollectJsService,
    private stripeService: OrgStripeService
  ) {}

  reloadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.reloadCart),
      withLatestFrom(this.store.select(CheckoutSelectors.selectCart)),
      map(([, cart]) => {
        return CheckoutActions.loadCart({ cartId: cart._id });
      })
    )
  );

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCart),
      withLatestFrom(
        this.store.select(AuthSelectors.getIsAuth),
        this.store.select(AppSelectors.getLayout)
      ),
      switchMap(([{ cartId }, isAuthenticated, layout]) => {
        if (isAuthenticated) {
          return this.apiService.getCheckoutSession(cartId).pipe(
            map(session =>
              CheckoutActions.loadCartSuccess({ cart: session.cart })
            ),
            catchError(error => of(CheckoutActions.loadCartFailure(error)))
          );
        } else {
          return this.apiService
            .getCartPublic(layout.organization, cartId)
            .pipe(map(cart => CheckoutActions.loadCartSuccess({ cart })));
        }
      })
    )
  );

  startCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.startCheckout),
      switchMap(({ cartId }) =>
        this.apiService.startCheckoutV2(cartId).pipe(
          tap(({ cart }) => {
            this.gtm.eventTrack('checkout-started', {
              category: 'checkout',
              label: 'checkout-started',
              value: cart.totalAmount,
              gtmCustom: {
                cart,
                items: cart?.lineItems?.map(item => ({
                  item_name: item.name,
                  item_id: item.sku,
                  quantity: item.quantity,
                  price: item.listPrice
                }))
              }
            });
          }),
          map(({ cart, paymentMethods }) =>
            CheckoutActions.startCheckoutSuccess({ cart, paymentMethods })
          ),
          catchError(error => of(CheckoutActions.startCheckoutFailure(error)))
        )
      )
    )
  );

  startCheckoutFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.startCheckoutFailure),
        tap(({ error }) => {
          console.error(error);
          this.notification.error(
            notificationMessages.InvalidCart.title,
            notificationMessages.InvalidCart.message
          );
        })
      ),
    { dispatch: false }
  );

  // this will be included un the start checkout response
  loadAddresses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadAddresses),
      switchMap(action =>
        this.apiService
          .getUserAddresses()
          .pipe(
            map(addresses =>
              CheckoutActions.loadAddressesSuccess({ addresses })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                'Error',
                'There was an error getting addresses.'
              );
              return of(CheckoutActions.loadAddressesFailure({ error }));
            })
          )
      )
    )
  );

  // this will return the cart after the coupon discount
  applyCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.applyCoupon),
      withLatestFrom(this.store.select(CheckoutSelectors.selectCart)),
      switchMap(([action, cart]) =>
        this.apiService.applyCouponV2(cart._id, action.code).pipe(
          tap(({ error }) => {
            if (error) {
              this.notification.info('Coupon was not applied', error);
            } else {
              this.notification.success('Coupon applied!', '');
            }
          }),
          map(({ cart, coupon, error }) => {
            if (error) {
              return CheckoutActions.applyCouponFailure();
            }
            return CheckoutActions.applyCouponSuccess({ cart, coupon });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', error);
            return of(CheckoutActions.applyCouponFailure());
          })
        )
      )
    )
  );

  // saveAddresses$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CheckoutActions.saveAddresses),
  //     fetch({
  //       run: action => {
  //         return this.apiService.saveAddresses(action.addressDTO).pipe(
  //           map(addresses => CheckoutActions.saveAddressesSuccess({ addresses }))
  //         );
  //       },

  //       onError: (action, error) => {
  //         console.error('Error', error);
  //         this.notification.error('Error', 'There was an error getting addresses.');
  //         return CheckoutActions.saveAddressesFailure({ error });
  //       }
  //     })
  //   )
  // );

  // setShippingAddress$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CheckoutActions.setShippingAddress),
  //     fetch({
  //       run: action => {
  //         return this.apiService.setShippingAddress(action.cartId, action.address).pipe(
  //           tap((res) => {
  //             this.notification.success('Successfully saved shipping address.', '');
  //           }),
  //           map(res => CheckoutActions.setShippingAddressSuccess({ res }))
  //         );
  //       },

  //       onError: (action, error) => {
  //         console.error('Error', error);
  //         this.notification.error('Error', 'There was an error setting shipping address.');
  //         return CheckoutActions.setShippingAddressFailure({ error });
  //       }
  //     })
  //   )
  // );

  finalizeCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.finalizeCheckout),
      withLatestFrom(
        this.store.select(CheckoutSelectors.selectAddresses),
        this.store.select(CheckoutSelectors.getSelectedPaymentMethodId),
        this.store.select(CheckoutSelectors.selectCvv)
      ),
      switchMap(
        ([
          { cartId, gateway, couponCode },
          addressState,
          paymentMethodId,
          cvv
        ]) => {
          const isNewShippingAddress = !(
            Number.isInteger(addressState.selectedAddressIndex) &&
            (addressState.selectedAddressIndex as number) >= 0
          );
          const address = isNewShippingAddress
            ? addressState.newAddress
            : addressState.data[addressState.selectedAddressIndex];
          if (!address) {
            return of(
              CheckoutActions.finalizeCheckoutFailure({
                error: new Error('Shipping address is incomplete')
              })
            );
          }
          let paymentMethod: Observable<{
            token: string;
            cvv?: string;
            [key: string]: any;
          }>;
          if (paymentMethodId !== 'new') {
            if (gateway === 'stripe') {
              paymentMethod = of({ token: paymentMethodId });
            } else if (gateway === 'nmi') {
              if (!isValidCVV(cvv.value)) {
                return [
                  CheckoutActions.finalizeCheckoutFailure({
                    error: new Error('CVV is incomplete')
                  }),
                  CheckoutActions.setCvv({ value: '', showError: true })
                ];
              }
              paymentMethod = of({
                token: paymentMethodId,
                cvv: cvv.value,
                type: 'vault'
              });
            }
          } else if (gateway === 'stripe') {
            paymentMethod = this.stripeService
              .tokenize()
              .pipe(map(pm => ({ token: pm.id })));
          } else if (gateway === 'nmi') {
            paymentMethod = from(
              this.collectJsService.tokenize().then(saveCard => {
                // TODO: move this saving card to customer logic to the backend
                // asynchronously saving the card to the customer vault
                this.store.dispatch(
                  AccountActions.addPaymentMethod({ token: saveCard.token })
                );
                return this.collectJsService.tokenize(); // we need a second token for the checkout
              })
            );
          }
          return paymentMethod.pipe(
            switchMap(pm =>
              this.apiService
                .finalizeCheckoutV2(cartId, {
                  paymentMethod: pm,
                  shippingAddress: address,
                  isNewShippingAddress,
                  couponCode
                })
                .pipe(
                  map(res =>
                    CheckoutActions.finalizeCheckoutSuccess({ order: res })
                  ),
                  catchError(error => {
                    return of(
                      CheckoutActions.finalizeCheckoutFailure({ error })
                    );
                  })
                )
            ),
            catchError(error => {
              return of(CheckoutActions.finalizeCheckoutFailure({ error }));
            })
          );
        }
      )
    )
  );

  finalizeCheckoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.finalizeCheckoutSuccess),
        tap(({ order }) => {
          this.gtm.eventTrack('order-created', {
            category: 'checkout',
            label: 'order-created',
            value: order.totalAmount,
            gtmCustom: {
              orderId: order.id,
              order
            }
          });

          if (order.hasUnsatisfiedQnrRequirements) {
            this.router.navigate(['consultation', 'start'], {
              queryParams: {
                orderId: order.id,
                redirectUri: '/orders/' + order.id,
                customerId:
                  (order.customer as ICustomerUser)._id || order.customer,
                verificationReqs: order.hasUnsatisfiedVerificationRequirements
              }
            });
          } else if (order.hasUnsatisfiedVerificationRequirements) {
            this.router.navigate(['verification', 'start'], {
              queryParams: {
                orderId: order.id,
                redirectUri: '/orders/' + order.id,
                customerId:
                  (order.customer as ICustomerUser)._id || order.customer
              }
            });
          } else {
            this.router.navigate(['orders', order.id]);
          }
        })
      ),
    { dispatch: false }
  );

  finalizeCheckoutFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.finalizeCheckoutFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages.OrderFailed.title,
            error.message || notificationMessages.OrderFailed.message
          );
        })
      ),
    { dispatch: false }
  );
}
